import { formatRelative } from "date-fns";

import { selectOutcomeStatusLine } from "./outcome-context";

import {
  OutcomeType,
  PageCategory,
  PageOutcomeType,
  PageType,
  SectionOutcomeType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { RootState } from "store/index";
import { selectCourseOutcomes, selectOutcomes, selectPageOutcomes } from "store/selectors/base";
import { selectPageById } from "store/selectors/page";
import { selectActivityById } from "store/selectors/section";
import { selectTopicById } from "store/selectors/topics";

// TODO: This should be adapted by page interaction mode, or explain interaction modes.
export const PAGE_EXPLANATION_CONTEXT = t("ai.page_explanation");

export const pageCategoryToExplanationContext: Record<PageCategory, string> = {
  activity_page: t("ai.topic_explanation"),
  course_resource: t("ai.course_resource_explanation"),
  instructor_workspace: t("ai.instructor_workspace_explanation"),
  assignment: t("ai.assignment_explanation"),
};

export const getPageCategoryContext = (page: PageType) => {
  return pageCategoryToExplanationContext[page.category];
};

export const selectPageStatusLine = (state: RootState, page: PageType) => {
  const outcomes = selectOutcomes(state);
  const courseOutcomes = selectCourseOutcomes(state);
  const pageOutcomes = selectPageOutcomes(state);
  return getPageStatusLine(page, pageOutcomes, courseOutcomes, outcomes);
};

export const getPageStatusLine = (
  page: PageType,
  pageOutcomes: PageOutcomeType[],
  courseOutcomes: SectionOutcomeType[],
  outcomes: OutcomeType[],
) => {
  const thisPageOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);
  const thisCourseOutcomes = courseOutcomes.filter((co) =>
    thisPageOutcomes.some((po) => po.section_outcome_id === co.id),
  );
  const thisOutcomes = outcomes.filter((o) =>
    thisCourseOutcomes.some((po) => po.outcome_id === o.id),
  );
  const outcomeStatusLine = selectOutcomeStatusLine(thisOutcomes);
  const status = page.released_at ? t("ai.page_status.released") : t("ai.page_status.unreleased");
  return `Page "${page.title}" (id "${page.id}") (${status}) using ${outcomeStatusLine}`;
};

export const selectPageMetadataContext = (state: RootState, pageId: string) => {
  const page = selectPageById(state, pageId);
  if (!page) {
    return "";
  }
  const activity = selectActivityById(state, page.activity_id);
  const topic = selectTopicById(state, activity?.topic_id);

  return [
    `The currently selected Page (id "${page.id}") has the following attributes:`,
    `- Title: "${page.title}"`,
    page.category === "activity_page" ? `- Activity Title: "${activity?.title}"` : "",
    page.category === "activity_page" ? `- Topic Title: "${topic?.title}"` : "",
    `- Release status: "${page.released_at ? "Released to students" : "Not released to students"}`,
    `- Due date: "${page.due_at ? formatRelative(page.due_at, new Date()) : "No due date"}`,
    `- Max points: "${page.max_points}"`,
    `- Interaction mode: "${page.interaction_mode}"`,
    page.locked_at
      ? `- Locked for students since: "${formatRelative(page.locked_at, new Date())}"`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
};
